import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type DropzoneFileInputProps = {
  label?: string;
  helperText?: string;
  value: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  disabled?: boolean;
  maxBytes?: number;
};

const DropzoneFileInput = ({
  label,
  helperText,
  value,
  onChange,
  accept = "image/*",
  disabled = false,
  maxBytes,
}: DropzoneFileInputProps) => {
  const { t } = useTranslation("common");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>("");

  const previewUrl = useMemo(() => {
    if (!value) return null;
    if (!value.type.startsWith("image/")) return null;
    return URL.createObjectURL(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const pick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const setFile = (file: File | null) => {
    setError("");
    if (!file) {
      onChange(null);
      return;
    }
    if (accept && accept.includes("image/") && !file.type.startsWith("image/")) {
      setError(t("uploader_invalid_type"));
      return;
    }
    if (typeof maxBytes === "number" && Number.isFinite(maxBytes)) {
      if (file.size > maxBytes) {
        setError(t("uploader_too_large"));
        return;
      }
    }
    onChange(file);
  };

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setFile(file);
  };

  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(true);
  };

  const onDragLeave: React.DragEventHandler<HTMLDivElement> = () => {
    setIsDragging(false);
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0]
      ? e.dataTransfer.files[0]
      : null;
    setFile(file);
  };

  const remove = () => {
    if (disabled) return;
    setError("");
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={pick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") pick();
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={[
          "w-full rounded-md border border-dashed px-4 py-4 text-sm bg-white dark:bg-gray-800",
          "transition-colors",
          disabled
            ? "opacity-60 cursor-not-allowed"
            : "cursor-pointer hover:border-blue-400",
          isDragging
            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/10"
            : "border-gray-300 dark:border-gray-600",
        ].join(" ")}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onInputChange}
          disabled={disabled}
          className="hidden"
        />

        {!value ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {t("uploader_drop_here")}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t("uploader_click_to_browse")}
              </div>
              {helperText && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {helperText}
                </div>
              )}
            </div>
            <div className="shrink-0 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200">
              {t("uploader_browse")}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 flex items-center justify-center shrink-0">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={t("reference_image")}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-xs text-gray-400">FILE</div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {value.name}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {(value.size / 1024).toFixed(0)} KB
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove();
              }}
              disabled={disabled}
              className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
            >
              {t("remove")}
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
};

export default DropzoneFileInput;

